import { Checkbox } from "@base-ui/react/checkbox";
import { CheckboxGroup } from "@base-ui/react/checkbox-group";
import { Check } from "lucide-react";
import { useCallback } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "./icon";

/**
 * Any number of options, as a wrap of pill chips. A2's `MultiChips`, on Base
 * UI's CheckboxGroup: each chip is a `checkbox` (A2 used `aria-pressed`
 * buttons), which is what a screen reader expects of "pick any of these".
 *
 * The selected chip is the inverse pill with a check, as in A2.
 */

export interface MultiChipOption<T extends string> {
  label: string;
  value: T;
}

export interface MultiChipsProps<T extends string> {
  className?: string;
  disabled?: boolean;
  /** The group's accessible name. */
  label: string;
  onValueChange: (value: T[]) => void;
  options: readonly MultiChipOption<T>[];
  value: readonly T[];
}

export function MultiChips<T extends string>({
  value,
  onValueChange,
  options,
  label,
  disabled,
  className,
}: MultiChipsProps<T>) {
  // CheckboxGroup reports plain strings; every one of them is an option value.
  const change = useCallback(
    (next: string[]) => {
      onValueChange(next as T[]);
    },
    [onValueChange]
  );
  return (
    <CheckboxGroup
      aria-label={label}
      className={cx("flex flex-wrap gap-2", className)}
      disabled={disabled}
      onValueChange={change}
      value={[...value]}
    >
      {options.map((option) => (
        <Checkbox.Root
          className="a-press inline-flex h-12 cursor-pointer select-none items-center gap-1.5 rounded-full bg-surface px-4 font-medium text-base text-ink-2 ring-1 ring-line transition-colors hover:ring-line-strong data-disabled:cursor-not-allowed data-checked:bg-inverse data-checked:text-on-inverse data-disabled:opacity-40 data-checked:ring-0"
          key={option.value}
          value={option.value}
        >
          <Checkbox.Indicator className="-ml-1 flex">
            <Icon bold icon={Check} size={16} />
          </Checkbox.Indicator>
          {option.label}
        </Checkbox.Root>
      ))}
    </CheckboxGroup>
  );
}
